import React, { useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../constants';

interface RecentRepair {
  makeName: string;
  modelName: string;
  customerName: string;
  customerMobile: string;
  customerArea: string;
  serviceSummary: string;
  orderDate: string;
  modelImageUrl?: string;
}

interface Props {
  modelId: number;
  modelName: string;
}


function relativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const diffDays = Math.floor((Date.now() - date.getTime()) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 14) return '1 week ago';
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return dateStr;
}

const ModelRepairsCarousel: React.FC<Props> = ({ modelId, modelName }) => {
  const [repairs, setRepairs] = useState<RecentRepair[]>([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Mouse drag-to-scroll
  const isDragging = useRef(false);
  const startX    = useRef(0);
  const scrollLeft = useRef(0);

  useEffect(() => {
    setLoading(true);
    setRepairs([]);
    fetch(`${API_BASE_URL}/Order/recent-repairs?days=90&limit=20&modelId=${modelId}`)
      .then(r => r.json())
      .then(data => {
        if (data.isSuccess && data.entity?.length) setRepairs(data.entity);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [modelId]);

  const onMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    startX.current = e.pageX - (scrollRef.current?.offsetLeft ?? 0);
    scrollLeft.current = scrollRef.current?.scrollLeft ?? 0;
    if (scrollRef.current) scrollRef.current.style.cursor = 'grabbing';
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX.current) * 1.2;
    scrollRef.current.scrollLeft = scrollLeft.current - walk;
  };

  const onMouseUp = () => {
    isDragging.current = false;
    if (scrollRef.current) scrollRef.current.style.cursor = 'grab';
  };

  if (loading || repairs.length === 0) return null;

  return (
    <div className="mb-4 mt-6">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3 px-1">
        <div className="w-1 h-5 bg-minto-500 rounded-full" />
        <h2 className="text-base md:text-lg font-semibold text-gray-800">
          Recent {modelName} Repairs
        </h2>
        <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
      </div>

      {/* Scrollable track */}
      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto pb-2 select-none"
        style={{
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          cursor: 'grab',
          WebkitOverflowScrolling: 'touch',
        }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
      >
        {/* Leading space */}
        <div className="flex-shrink-0 w-1" />

        {repairs.map((repair, i) => {
          const firstName      = repair.customerName?.split(' ')[0] ?? '?';
          const initial        = firstName[0]?.toUpperCase() ?? '?';
          const services       = repair.serviceSummary?.split(',').map(s => s.trim()) ?? [];
          const primaryService = services[0] ?? 'Repair';
          const extraCount     = services.length - 1;

          return (
            <div
              key={i}
              className="flex-shrink-0 rounded-2xl p-4 flex flex-col gap-2"
              style={{
                width: '190px',
                scrollSnapAlign: 'start',
                background: '#ffffff',
                border: '1.5px solid #d1d5db',
              }}
            >
              {/* Avatar + name */}
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold bg-white border-2 border-gray-300 text-gray-700">
                  {initial}
                </div>
                <span className="text-sm font-semibold truncate" style={{ color: '#c0007a' }}>{firstName}</span>
              </div>

              {/* Service */}
              <div className="text-xs font-semibold text-fuchsia-700 truncate">
                🔧 {primaryService}
                {extraCount > 0 && (
                  <span className="text-fuchsia-400 font-normal"> +{extraCount}</span>
                )}
              </div>

              {/* Area + date */}
              <div className="flex flex-col gap-0.5 mt-auto">
                {repair.customerArea && (
                  <span className="text-xs text-gray-500 truncate">📍 {repair.customerArea}</span>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">{relativeTime(repair.orderDate)}</span>
                  <div className="w-5 h-5 rounded-full bg-white/70 flex items-center justify-center">
                    <svg className="w-3 h-3 text-fuchsia-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Trailing space */}
        <div className="flex-shrink-0 w-1" />
      </div>

      {/* Hide scrollbar in webkit */}
      <style>{`
        div[style*="scrollbarWidth"]::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
};

export default ModelRepairsCarousel;
